import React, { useContext, useMemo, useState } from "react";
import { ListChecks, OctagonX } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderMonitor from "../api";
import StatTiles from "../components/StatTiles";
import useStats from "../components/useStats";
import apiReminderInstance from "./api";
import { loadInstanceFilterDependencies } from "./features/instance.dependencies";
import { instanceFilterFields } from "./features/instance.filter";
import { getInstanceColumns } from "./instance-list/instanceColumns";
import InstanceRowActions from "./instance-list/InstanceRowActions";
import InstanceViewDrawer from "./instance-dialogs/InstanceViewDrawer";

// Reminder Monitor -> Cases (tbl_rem_instance): one row per reminder case the engine opened (rule x user x record).
const Instance = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderInstance.getAll, { loadError: "Failed to load reminder cases.", initialSort: { key: "id", direction: "desc" } });
  const { stats, loading: statsLoading } = useStats(apiReminderInstance.getStats, list.refreshKey);
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(null);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const openView = async (row) => {
    setBusy({ id: row.id, action: "view" });
    try {
      const [caseRes, deliveriesRes] = await Promise.all([apiReminderInstance.getById(row.id), apiReminderMonitor.getCaseDeliveries(row.id)]);
      setView({ record: caseRes.data?.data, deliveries: deliveriesRes.data?.data ?? [] });
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  /** Stops OPEN cases (`rows`: one row, or the selected ids). */
  const handleStop = async (rows) => {
    const ids = rows.map((r) => (typeof r === "object" ? r.id : r));
    const one = rows.length === 1 && typeof rows[0] === "object" ? rows[0] : null;
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Stop Cases" : "Stop Case",
      message: one ? `Stop this case of "${one.rule?.name ?? "the rule"}"? No further reminders go out for it.` : `Stop ${ids.length} selected cases? No further reminders go out for them; cases that are not open are left as they are.`,
      confirmText: "Stop",
      variant: "danger",
      icon: <OctagonX size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    setBusy(one ? { id: one.id, action: "stop" } : null);
    try {
      const res = await apiReminderInstance.stop({ ids });
      showAlert({ type: "success", message: res.data?.message || "Stopped." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = useMemo(() => getInstanceColumns(list.start), [list.start]);
  const s = stats?.by_status ?? {};
  const tiles = [
    { label: "Open now", value: stats?.open_now, tone: "brand", hint: "Still sending" },
    { label: "Resolved", value: s.RESOLVED, tone: "success", hint: "The record stopped matching" },
    { label: "Exhausted", value: s.EXHAUSTED, tone: "warning", hint: "Every reminder sent" },
    { label: "Stopped", value: s.STOPPED, tone: "muted", hint: "By hand, rule or user" },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <StatTiles items={tiles} loading={statsLoading} note={stats ? `Cases opened ${stats.from} to ${stats.to} (${stats.timezone}).` : null} />
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          resetSearchTrigger={list.resetTrigger}
          canCreate={false}
          selectedCount={list.selectedRows.length}
          bulkActions={canManage ? [{ key: "stop", label: `Stop (${list.selectedRows.length})`, icon: OctagonX, variant: "danger", onClick: () => handleStop(list.selectedRows) }] : undefined}
          onClearSelection={list.resetSelection}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={instanceFilterFields} dependenciesLoader={loadInstanceFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={ListChecks}
              title={list.hasQuery ? "No cases match" : "No reminder cases yet"}
              message={list.hasQuery ? "No cases match your search or filters. Try adjusting them." : "A case opens when an active rule finds a record to remind about. Activate a rule in Reminder Designer to see cases here."}
              showBack={false}
              showRefresh
              onRefresh={list.handleRefresh}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={(row) => <InstanceRowActions row={row} canManage={canManage} busy={busy} onView={openView} onStop={(target) => handleStop([target])} />}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      {view?.record && <InstanceViewDrawer record={view.record} deliveries={view.deliveries} onClose={() => setView(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Instance;
